import { IPD_CONSCIOUSNESS_LEVELS, IPD_IV_SITE_CONDITIONS, IPD_OXYGEN_DEVICES } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { Field, Input, Switch, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import {
  NUMERIC_READINGS,
  readingFieldsFor,
  readingRange,
  readingRequired,
  type ConsciousnessLevel,
  type IvSiteCondition,
  type NumericReading,
  type OxygenDevice,
  type ReadingDraft,
  type ReadingField,
} from './record-task';
import { consciousnessLabel, deviceLabel, readingLabel, siteConditionLabel } from './task-labels';

function isNumeric(field: ReadingField): field is NumericReading {
  return (NUMERIC_READINGS as readonly string[]).includes(field);
}

export function ReadingsFields({
  taskKind,
  value,
  onChange,
  errorOf,
}: {
  taskKind: string;
  value: ReadingDraft;
  onChange: (next: ReadingDraft) => void;
  errorOf: (field: ReadingField) => string | undefined;
}) {
  const fields = readingFieldsFor(taskKind);
  const numeric = fields.filter(isNumeric);
  const setNumber = (field: NumericReading, text: string) => onChange({ ...value, numbers: { ...value.numbers, [field]: text } });

  return (
    <fieldset className="space-y-3 rounded-lg border p-3">
      <legend className="px-1 text-sm font-medium">{tr('ipd', 'io_task_readings')}</legend>

      {fields.includes('siteCondition') && (
        <Field label={readingLabel('siteCondition')} required error={errorOf('siteCondition')}>
          <ChoiceButtons<IvSiteCondition>
            layout="chips"
            value={value.siteCondition}
            onChange={(next) => onChange({ ...value, siteCondition: next })}
            label={readingLabel('siteCondition')}
            options={IPD_IV_SITE_CONDITIONS.map((condition) => ({ value: condition, label: siteConditionLabel(condition) }))}
          />
        </Field>
      )}

      {numeric.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {numeric.map((field) => {
            const range = readingRange(field);
            return (
              <Field key={field} label={readingLabel(field)} required={readingRequired(taskKind, field)} error={errorOf(field)}>
                <Input
                  type="number"
                  inputMode={range.integer ? 'numeric' : 'decimal'}
                  step={range.integer ? 1 : 0.1}
                  min={range.min}
                  max={range.max}
                  value={value.numbers[field] ?? ''}
                  onChange={(event) => setNumber(field, event.target.value)}
                />
              </Field>
            );
          })}
        </div>
      )}

      {fields.includes('consciousness') && (
        <Field label={readingLabel('consciousness')} error={errorOf('consciousness')}>
          <ChoiceButtons<ConsciousnessLevel>
            value={value.consciousness}
            onChange={(next) => onChange({ ...value, consciousness: next === value.consciousness ? '' : next })}
            label={readingLabel('consciousness')}
            className="flex-wrap [&>button]:min-w-[5.5rem]"
            options={IPD_CONSCIOUSNESS_LEVELS.map((level) => ({ value: level, label: consciousnessLabel(level) }))}
          />
        </Field>
      )}

      {fields.includes('device') && (
        <Field label={readingLabel('device')} error={errorOf('device')}>
          <ChoiceButtons<OxygenDevice>
            layout="chips"
            value={value.device}
            onChange={(next) => onChange({ ...value, device: next === value.device ? '' : next })}
            label={readingLabel('device')}
            options={IPD_OXYGEN_DEVICES.map((device) => ({ value: device, label: deviceLabel(device) }))}
          />
        </Field>
      )}

      {fields.includes('reaction') && (
        <Switch checked={value.reaction} onCheckedChange={(checked) => onChange({ ...value, reaction: checked })} label={readingLabel('reaction')} />
      )}

      {fields.includes('reactionNotes') && value.reaction && (
        <Field label={readingLabel('reactionNotes')} required error={errorOf('reactionNotes')}>
          <Textarea value={value.reactionNotes} maxLength={600} onChange={(event) => onChange({ ...value, reactionNotes: event.target.value })} />
        </Field>
      )}
    </fieldset>
  );
}
